import { TeButton, TeContainer, TeContainerSlim } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { PlaceholderContainer } from '@topcon/topcon-utils';
import { useState } from 'react';

import { Section } from '../../../utils/styling';
import Header from '../../../components/Header';
import {
  useCustomApplications,
  useCustomApplication,
  invalidateCustomApplications,
} from './customApplications.queries';
import CustomApplicationCard from './CustomApplicationCard';
import AddEditCustomApplicationModal from './AddEditCustomApplicationModal';
import DeleteCustomApplicationModal from './DeleteCustomApplicationModal';
import { ClientBody } from '../../../api/generated/pap';

const CustomApplications = () => {
  const { t } = useTranslation('settings');

  const { data, isFetching } = useCustomApplications();
  const customApplications =
    data?.items?.sort((appA, appB) =>
      String(appB.modifiedAt).localeCompare(String(appA.modifiedAt))
    ) ?? [];
  const noContent = isFetching || !customApplications.length;

  const [selectedCustomApp, setSelectedCustomApp] = useState<ClientBody | null>(null);
  // track the selected app id so we can fetch full details when editing
  const [selectedCustomAppId, setSelectedCustomAppId] = useState<string | null>(null);
  const [addEditCustomAppModalOpen, setAddEditCustomAppModalOpen] = useState(false);
  const [deleteCustomAppModalOpen, setDeleteCustomAppModalOpen] = useState(false);

  const openCustomAppAddEditModal = (app: ClientBody | null) => {
    setSelectedCustomApp(app);
    setSelectedCustomAppId(app?.id ?? null);
    setAddEditCustomAppModalOpen(true);
  };

  const openCustomAppDeleteModal = (app: ClientBody | null) => {
    setSelectedCustomApp(app);
    setDeleteCustomAppModalOpen(true);
  };

  const handleAddEditCustomAppModalClose = (shouldUpdate: boolean) => {
    setAddEditCustomAppModalOpen(false);
    setSelectedCustomApp(null);
    setSelectedCustomAppId(null);
    if (shouldUpdate) invalidateCustomApplications();
  };

  const handleDeleteCustomAppModalClose = (shouldUpdate: boolean) => {
    setDeleteCustomAppModalOpen(false);
    setSelectedCustomApp(null);
    setSelectedCustomAppId(null);
    if (shouldUpdate) invalidateCustomApplications();
  };

  // fetch full details for the selected app id; enabled only when id is set
  const { data: selectedCustomAppDetails } = useCustomApplication(selectedCustomAppId ?? undefined);

  const renderEmptyState = () => {
    return (
      <PlaceholderContainer
        icon="language"
        text={t('customApplications.noApplicationsYet')}
        secondaryText={t('clickButtonToAddOne')}
      />
    );
  };

  const renderLoader = () => {
    return <PlaceholderContainer hasLoader text={t('customApplications.loading')} />;
  };

  const renderNoContent = () => {
    return (
      <TeContainerSlim
        backgroundColor="background.secondary"
        borderRadius={4}
        fill
        justifyContent="center"
        alignItems="center"
      >
        {isFetching ? renderLoader() : renderEmptyState()}
      </TeContainerSlim>
    );
  };

  const renderCustomApplications = () => {
    // Placeholder for actual custom applications rendering
    return customApplications?.map((app: ClientBody) => (
      <CustomApplicationCard
        key={app.id}
        application={app}
        disabled={isFetching}
        onEdit={openCustomAppAddEditModal}
        onDelete={openCustomAppDeleteModal}
      />
    ));
  };

  return (
    <TeContainer column grow gap="xs" padding="xs" overflow="auto" alignItems="center">
      <Header text={t('customApplications.title')}>
        <TeButton
          text={t('customApplications.addButton')}
          teclick={() => openCustomAppAddEditModal(null)}
          icon="add"
          disabled={isFetching}
        />
      </Header>

      <Section>
        <TeContainer column grow gap="s">
          {noContent ? renderNoContent() : renderCustomApplications()}
        </TeContainer>
      </Section>

      {addEditCustomAppModalOpen && (
        <AddEditCustomApplicationModal
          // prefer fetched details (when available), fall back to list item
          customApplication={selectedCustomAppDetails ?? selectedCustomApp}
          open={addEditCustomAppModalOpen}
          onClose={handleAddEditCustomAppModalClose}
        />
      )}

      {selectedCustomApp && (
        <DeleteCustomApplicationModal
          open={deleteCustomAppModalOpen}
          onClose={handleDeleteCustomAppModalClose}
          customApplication={selectedCustomApp}
        />
      )}
    </TeContainer>
  );
};

export default CustomApplications;
